/**
 * THE FILE — chips. Two kinds, and they mean different things.
 *
 *   · A CONTEXT CHIP names what a piece of work is FOR — the client, the
 *     engagement. It sits in the composer's bar, reads as a fact already in
 *     place ("Client: Northwind Foods"), and changes with one choice.
 *   · A RECORD CHIP is a file the accountant has in hand, with what it IS
 *     beside it. It is a separable object, so it has an edge and no radius.
 *
 * BOTH ARE NATIVE SELECTS UNDER A CHIP FACE. The chip draws the words; an
 * invisible `<select>` laid exactly over it receives the click, the tap and
 * the keyboard. So the choice keeps everything the platform already does
 * well — type-ahead, the phone's own picker, focus order, the accessible name
 * — and nothing here re-implements a listbox to get back to where the browser
 * started. The focus ring is drawn on the chip, because the select itself is
 * transparent and a ring around nothing is no ring.
 *
 * The select is 16px even though it is never seen: below that, a phone zooms
 * the page when it takes focus.
 */
import { clsx } from "clsx";
import { ChevronDown, FileText, X } from "lucide-react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

/** The ring the sheet gives every focused control, moved onto the chip that holds the select. */
const RING_ON_CHIP =
  "has-[select:focus-visible]:outline has-[select:focus-visible]:outline-2 has-[select:focus-visible]:outline-offset-2 has-[select:focus-visible]:outline-forest";

const OVERLAY_SELECT =
  "absolute inset-0 h-full w-full cursor-pointer appearance-none text-base opacity-0 disabled:cursor-not-allowed";

export interface ChipSelectProps extends Omit<ComponentPropsWithoutRef<"select">, "className" | "children" | "aria-label"> {
  /** What the chip names — "Client". It is also the select's accessible name. */
  label: string;
  /** The current choice, as words. */
  display: ReactNode;
  /** Nothing is chosen yet: the value reads quieter, and is not set in weight. */
  empty?: boolean | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/** A context chip: "Label: value ▾", and one choice to change it. */
export function ChipSelect({ label, display, empty, disabled, children, className, ...rest }: ChipSelectProps) {
  return (
    <span
      className={clsx(
        "relative inline-flex h-11 min-w-0 max-w-full items-center gap-1.5 rounded-control border border-line-strong pl-3 pr-2.5 text-[13px] text-ink-soft transition-colors duration-150",
        disabled ? "text-stone" : "hover:bg-band",
        RING_ON_CHIP,
        className
      )}
    >
      <span aria-hidden="true" className="shrink-0">{label}:</span>
      <span aria-hidden="true" className={clsx("min-w-0 truncate", empty || disabled ? "text-stone" : "font-semibold text-ink")}>
        {display}
      </span>
      <ChevronDown aria-hidden="true" size={14} className="shrink-0 text-stone" />
      <select aria-label={label} disabled={disabled} className={OVERLAY_SELECT} {...rest}>
        {children}
      </select>
    </span>
  );
}

/**
 * THE FACE OF A CONTEXT CHIP THAT OFFERS A WAY FORWARD instead of a choice —
 * "Client: Add your first client". Dashed, because a dashed edge reads as
 * "something goes here". The link itself is the caller's (a router link); the
 * chip lends it only its face, so the bar keeps its shape when a list is empty.
 */
export const CHIP_ACTION =
  "inline-flex h-11 max-w-full items-center gap-1.5 rounded-control border border-dashed border-rule-dashed px-3 text-[13px] text-ink-soft transition-colors duration-150 hover:border-forest hover:text-ink";

export interface RecordChipProps {
  /** The file's own name, set as a reference. */
  name: string;
  /** What the file is (a `KindSelect`), or a sentence about why it cannot be used. */
  children?: ReactNode;
  onRemove: () => void;
  disabled?: boolean | undefined;
}

/**
 * A file in hand, and what it is.
 *
 * On a phone it takes the full width and its remove control grows to a 44px
 * target; at a desk it is a compact chip among others.
 */
export function RecordChip({ name, children, onRemove, disabled }: RecordChipProps) {
  return (
    <li className="flex min-w-0 max-w-full items-center gap-2 border border-rule-soft bg-canvas py-1 pl-2.5 pr-1 max-sm:w-full max-sm:py-1.5">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        {/* The icon travels with the name, so it sits beside it on every line length. */}
        <span className="inline-flex min-w-0 max-w-[16rem] items-center gap-2 max-sm:max-w-full" title={name}>
          <FileText aria-hidden="true" size={14} className="shrink-0 text-stone" />
          <span className="min-w-0 truncate font-mono text-[12px] text-ink">{name}</span>
        </span>
        {children}
      </div>
      <button
        type="button"
        aria-label={`Remove ${name}`}
        disabled={disabled}
        onClick={onRemove}
        className="flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-control text-stone transition-colors duration-150 hover:bg-band hover:text-ink disabled:opacity-50 max-sm:h-11 max-sm:w-11"
      >
        <X aria-hidden="true" size={14} />
      </button>
    </li>
  );
}

export interface KindSelectProps extends Omit<ComponentPropsWithoutRef<"select">, "className" | "children" | "aria-label"> {
  /** The accessible name — what is being asked about which file. */
  label: string;
  display: ReactNode;
  /** Nothing said yet. Shown in the attention words, because the work cannot open until it is. */
  empty?: boolean | undefined;
  children: ReactNode;
}

/** What a file is, as a 2px tag that is also a choice. */
export function KindSelect({ label, display, empty, disabled, children, ...rest }: KindSelectProps) {
  return (
    <span
      className={clsx(
        "relative inline-flex h-7 items-center gap-1 rounded-tag px-2 text-[12px] font-medium max-sm:h-11 max-sm:px-3",
        empty ? "bg-att-tint text-att-text" : "bg-forest-tint text-forest",
        disabled && "opacity-60",
        RING_ON_CHIP
      )}
    >
      <span aria-hidden="true">{display}</span>
      <ChevronDown aria-hidden="true" size={12} className="shrink-0" />
      <select aria-label={label} disabled={disabled} className={OVERLAY_SELECT} {...rest}>
        {children}
      </select>
    </span>
  );
}
